import * as React from 'react';
import { getSession, type Session } from '@/lib/auth';

/**
 * SPA-lifetime session cache.
 *
 * Two paths feed the cache:
 *
 *   1. On mount, `refresh()` calls `getSession` which probes
 *      `/v1/account` with credentials. Both auth shapes resolve
 *      cleanly: wallet via the Authorization JWT, OAuth via the
 *      rs_session cookie (H6.9 loosened the gate to accept session
 *      cookies in addition to API key + wallet JWT). Brief 63 +
 *      brief 65 made this the SOLE OAuth hydration path: the
 *      server-side /auth/callback handler now mints rs_session
 *      itself and 302s into the SPA at the redirect_to URL, so
 *      the SPA's first render runs the cold-reload probe with
 *      the cookie already in place.
 *
 *   2. WalletSignIn calls `seed()` after `mintWalletJwt()` writes
 *      the JWT to sessionStorage. That avoids the latency of a
 *      probe round-trip on the wallet path. (The OAuth path no
 *      longer uses seed — brief 65 retired the SPA's
 *      /auth/callback component along with /auth/exchange.)
 *
 * `loading` is true on first mount until the initial probe resolves
 * AND no seed has been pushed in. `seed()` flips loading to false
 * immediately so the wallet path's redirect doesn't render a
 * transient "Loading…" placeholder.
 */

interface SessionState {
  session: Session | null;
  loading: boolean;
  refresh: () => Promise<void>;
  seed: (next: Session) => void;
  clear: () => void;
}

const SessionContext = React.createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  // Skip the initial probe if a sign-in page already seeded the
  // context. The probe is for cold reloads — it duplicates network
  // work mid-signin otherwise.
  const seededRef = React.useRef(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const next = await getSession();
      setSession(next);
    } finally {
      setLoading(false);
    }
  }, []);

  const seed = React.useCallback((next: Session) => {
    seededRef.current = true;
    setSession(next);
    setLoading(false);
  }, []);

  const clear = React.useCallback(() => {
    seededRef.current = false;
    setSession(null);
  }, []);

  React.useEffect(() => {
    if (seededRef.current) return;
    void refresh();
  }, [refresh]);

  const value = React.useMemo<SessionState>(
    () => ({ session, loading, refresh, seed, clear }),
    [session, loading, refresh, seed, clear],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const ctx = React.useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
